import React from 'react';
import './ResumePrint.css';

const DEFAULT_SECTION_ORDER = ['summary', 'skills', 'experience', 'projects', 'education'];

const SECTION_LABELS = {
  summary: 'Summary',
  skills: 'Skills',
  experience: 'Experience',
  projects: 'Projects',
  education: 'Education',
};

/* ── Helpers ────────────────────────────────────── */

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const str = String(dateStr);
  if (str.toLowerCase() === 'present') return 'Present';
  const [year, month] = str.split('-');
  if (!year || !month) return str;  // year-only falls through gracefully
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const renderDates = (start, end) => {
  if (!start && !end) return '';
  if (start && (!end || String(end).toLowerCase() === 'present'))
    return formatDate(start) + ' – Present';
  if (!start && end) return 'Until ' + formatDate(end);
  return `${formatDate(start)} – ${formatDate(end)}`;
};

const descriptionToHtml = (desc) => {
  if (Array.isArray(desc)) {
    if (desc.length === 0) return '';
    return '<ul><li>' + desc.join('</li><li>') + '</li></ul>';
  }
  return desc || '';
};

/* ── Section Renderers ──────────────────────────── */

const SummarySection = ({ data }) => {
  if (!data) return null;
  return (
    <div className="resume-print__section">
      <h2 className="resume-print__section-title">{SECTION_LABELS.summary}</h2>
      <div
        className="resume-print__summary"
        dangerouslySetInnerHTML={{ __html: data }}
      />
    </div>
  );
};

const SkillsSection = ({ data }) => {
  if (!data || data.length === 0) return null;

  const renderSkill = (skill) => {
    if (typeof skill === 'string') {
      const colonIdx = skill.indexOf(':');
      if (colonIdx !== -1) {
        return (
          <>
            <span className="resume-print__skill-title">
              {skill.substring(0, colonIdx + 1)}
            </span>
            {skill.substring(colonIdx + 1)}
          </>
        );
      }
      return skill;
    }
    const desc = Array.isArray(skill.description)
      ? skill.description.join(', ')
      : skill.description;
    return (
      <>
        {skill.title && (
          <span className="resume-print__skill-title">{skill.title}: </span>
        )}
        {desc}
      </>
    );
  };

  return (
    <div className="resume-print__section">
      <h2 className="resume-print__section-title">{SECTION_LABELS.skills}</h2>
      <ul className="resume-print__skills-list">
        {data.map((skill, idx) => (
          <li key={idx}>{renderSkill(skill)}</li>
        ))}
      </ul>
    </div>
  );
};

const ExperienceSection = ({ data }) => {
  if (!data || data.length === 0) return null;
  return (
    <div className="resume-print__section">
      <h2 className="resume-print__section-title">{SECTION_LABELS.experience}</h2>
      {data.map((exp, idx) => (
        <div key={idx} className="resume-print__item">
          <div className="resume-print__item-header">
            <div className="resume-print__item-title">
              {exp.title}
              {exp.company && (
                <span className="resume-print__item-subtitle">
                  {' '}— {exp.company}
                </span>
              )}
            </div>
            {(exp.location || exp.dates || exp.start_date) && (
              <div className="resume-print__item-right">
                {exp.location}
                {(exp.dates || exp.start_date) && (
                  <span className="resume-print__item-dates">
                    {exp.location ? ' (' : ''}
                    {exp.dates ? exp.dates : renderDates(exp.start_date, exp.end_date)}
                    {exp.location ? ')' : ''}
                  </span>
                )}
              </div>
            )}
          </div>
          {exp.tech_stack && exp.tech_stack.trim() !== '' && (
            <div className="resume-print__item-techstack">
              <span className="resume-print__item-techstack-label">Tech Stack: </span>
              {exp.tech_stack}
            </div>
          )}
          <div
            className="resume-print__desc"
            dangerouslySetInnerHTML={{
              __html: descriptionToHtml(exp.description),
            }}
          />
        </div>
      ))}
    </div>
  );
};

const ProjectsSection = ({ data }) => {
  if (!data || data.length === 0) return null;
  return (
    <div className="resume-print__section">
      <h2 className="resume-print__section-title">{SECTION_LABELS.projects}</h2>
      {data.map((proj, idx) => (
        <div key={idx} className="resume-print__item">
          <div className="resume-print__item-header">
            <div className="resume-print__item-title">
              {proj.title}
              {proj.links && proj.links.length > 0 && (
                <span className="resume-print__item-links">
                  {proj.links.map((link, lIdx) => (
                    <React.Fragment key={lIdx}>
                      {lIdx > 0 && (
                        <span className="resume-print__item-links-sep">|</span>
                      )}
                      <a href={link.url} target="_blank" rel="noreferrer">
                        {link.label}
                      </a>
                    </React.Fragment>
                  ))}
                </span>
              )}
            </div>
          </div>
          {proj.tech_stack && proj.tech_stack.trim() !== '' && (
            <div className="resume-print__item-techstack">
              <span className="resume-print__item-techstack-label">Tech Stack: </span>
              {proj.tech_stack}
            </div>
          )}
          <div
            className="resume-print__desc"
            dangerouslySetInnerHTML={{
              __html: descriptionToHtml(proj.description),
            }}
          />
        </div>
      ))}
    </div>
  );
};

const EducationSection = ({ data }) => {
  if (!data || data.length === 0) return null;
  return (
    <div className="resume-print__section">
      <h2 className="resume-print__section-title">{SECTION_LABELS.education}</h2>
      {data.map((edu, idx) => (
        <div key={idx} className="resume-print__item">
          <div className="resume-print__item-header">
            <div className="resume-print__item-title">
              {edu.degree}
              {edu.school && (
                <span className="resume-print__item-subtitle">
                  {' '}— {edu.school}
                </span>
              )}
            </div>
            {edu.location && (
              <div className="resume-print__item-right">{edu.location}</div>
            )}
          </div>
          <div className="resume-print__edu-details">
            {edu.dates ? edu.dates : renderDates(edu.start_date, edu.end_date)}
          </div>
        </div>
      ))}
    </div>
  );
};

/* Map section keys → renderer components */
const SECTION_RENDERERS = {
  summary: SummarySection,
  skills: SkillsSection,
  experience: ExperienceSection,
  projects: ProjectsSection,
  education: EducationSection,
};

/* ── Main Component ─────────────────────────────── */

const ResumePrint = React.forwardRef(({ data, sectionOrder, fontSize = 14 }, ref) => {
  if (!data) return null;

  const order =
    sectionOrder && Array.isArray(sectionOrder) && sectionOrder.length > 0
      ? sectionOrder
      : DEFAULT_SECTION_ORDER;

  const personalInfo = data.personalInfo;

  return (
    <div className="resume-print" ref={ref} style={{ '--resume-base-size': `${fontSize}px`, '--resume-print-font-size': `${(fontSize / 1.333).toFixed(2)}pt` }}>
      {/* ── Header ── */}
      {personalInfo && (
        <header className="resume-print__header">
          <h1 className="resume-print__name">
            {personalInfo.name || 'Your Name'}
          </h1>
          {personalInfo.role && (
            <p className="resume-print__role">{personalInfo.role}</p>
          )}
          <div className="resume-print__contact">
            {personalInfo.location && <span>{personalInfo.location}</span>}
            {personalInfo.location && (personalInfo.email || personalInfo.phone) && (
              <span className="resume-print__contact-sep">—</span>
            )}
            {personalInfo.email && <span>{personalInfo.email}</span>}
            {personalInfo.email && personalInfo.phone && (
              <span className="resume-print__contact-sep">—</span>
            )}
            {!personalInfo.email && personalInfo.location && personalInfo.phone && (
              <span className="resume-print__contact-sep">—</span>
            )}
            {personalInfo.phone && <span>{personalInfo.phone}</span>}
            {(personalInfo.links || []).map((link, idx) => (
              <React.Fragment key={idx}>
                {((idx === 0 &&
                  (personalInfo.location || personalInfo.email || personalInfo.phone)) ||
                  idx > 0) && (
                  <span className="resume-print__contact-sep">—</span>
                )}
                {link.url ? (
                  <a href={link.url} target="_blank" rel="noreferrer">
                    {link.label}
                  </a>
                ) : (
                  <span>{link.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </header>
      )}

      {/* ── Dynamic Sections ── */}
      {order.map((key) => {
        const Renderer = SECTION_RENDERERS[key];
        if (!Renderer) return null;
        return <Renderer key={key} data={data[key]} />;
      })}
    </div>
  );
});

ResumePrint.displayName = 'ResumePrint';

export default ResumePrint;
